import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, FileText, Utensils, Coffee, Sun, ChefHat, MessageSquare } from 'lucide-react';
import { MealInfo } from '../daily-tile';
import { useIsMobile } from '@/hooks/use-mobile';
import RecipeThumbnail from '@/components/ui/recipe-thumbnail';

interface MealItemTileProps {
  meal: MealInfo;
  showMealType?: boolean;
  showReviewButton?: boolean;
  isYesterday?: boolean;
}

const MealItemTile: React.FC<MealItemTileProps> = ({ 
  meal, 
  showMealType = false, 
  showReviewButton = false,
  isYesterday = false 
}) => {
  const isMobile = useIsMobile();
  
  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (meal.onRemove && meal.mealPlanId) {
      meal.onRemove(meal.mealPlanId);
    } else if (meal.onRemoveNote && meal.mealNoteId) {
      meal.onRemoveNote(meal.mealNoteId);
    }
  };

  const deleteButtonClasses = `
    ${isMobile ? 'h-8 w-8 min-w-[2rem]' : 'h-5 w-5'} 
    p-0 shrink-0 
    ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} 
    transition-opacity
  `;

  const canRemove = (meal.onRemove && meal.mealPlanId) || (meal.onRemoveNote && meal.mealNoteId);

  return (
    <Card className="p-2 bg-card border-border/50 w-full group hover:bg-primary/5 transition-colors">
      <div className="flex items-start gap-2 min-w-0">
        {/* Thumbnail or Icon */}
        <div className="shrink-0">
          {meal.type === 'recipe' && (meal.userImageUrl || meal.imageUrl || meal.storageImageUrl) ? (
            <RecipeThumbnail 
              userImageUrl={meal.userImageUrl}
              userImagePath={meal.userImagePath}
              storageImageUrl={meal.storageImageUrl}
              storageImagePath={meal.storageImagePath}
              imageUrl={meal.imageUrl}
              recipeName={meal.name}
              size="lg"
              mealType={meal.mealType}
            />
          ) : meal.type === 'note' ? (
            <div className="w-14 h-14 rounded-md bg-secondary/20 flex items-center justify-center">
              <FileText className="h-5 w-5 text-secondary-foreground" />
            </div>
          ) : meal.mealType === 'breakfast' ? (
            <div className="w-14 h-14 rounded-md bg-accent/20 flex items-center justify-center">
              <Coffee className="h-5 w-5 text-accent-foreground" />
            </div>
          ) : meal.mealType === 'lunch' ? (
            <div className="w-14 h-14 rounded-md bg-primary/20 flex items-center justify-center">
              <Sun className="h-5 w-5 text-primary-foreground" />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-md bg-muted/40 flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 overflow-hidden">
              {showMealType && (
                <p className="text-xs text-muted-foreground mb-1 capitalize font-medium">
                  {meal.mealType}
                </p>
              )}
              
              {meal.type === 'note' ? (
                <div className="space-y-1">
                  <p className="font-medium text-sm truncate italic text-muted-foreground">
                    {meal.name}
                  </p>
                  {meal.authorFirstName && (
                    <p className="text-xs text-muted-foreground">
                      by {meal.authorFirstName}
                    </p>
                  )}
                </div>
              ) : (
                <Link 
                  to={meal.type === 'recipe' ? (showReviewButton ? `/recipe/${meal.id}#recipe-feedback` : `/recipe/${meal.id}`) : `/composition/${meal.id}`}
                  className="block hover:text-primary transition-colors"
                >
                  <div className="space-y-1">
                    <div className="flex items-start gap-2">
                      <p className="font-semibold text-base line-clamp-2 flex-1 text-foreground leading-tight">
                        {meal.name}
                      </p>
                      {showReviewButton && meal.type === 'recipe' && (
                        <div className="shrink-0 h-8 w-8 flex items-center justify-center bg-amber-50 border border-amber-200 rounded-md">
                          <MessageSquare className="h-4 w-4 text-amber-600" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      planned by {meal.authorFirstName || 'someone'}
                    </p>
                  </div>
                </Link>
              )}
            </div>
            
            {/* Delete Button */}
            {canRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                className={deleteButtonClasses}
                aria-label={`Remove ${meal.name}`}
              >
                <X className={isMobile ? "h-4 w-4" : "h-3 w-3"} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default MealItemTile;
